import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { FaTrash } from 'react-icons/fa';
import 'react-tooltip/dist/react-tooltip.css';
import './Cart.css';

// Zelle payment only - no external payment scripts needed

function Cart({ items, removeFromCart, clearCart }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [showZelleInstructions, setShowZelleInstructions] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
  };

  const renderSubscriptionDetails = (item) => {
    if (item.isSubscription) {
      return (
        <div className="subscription-details">
          <p className="text-sm text-gray-600 mt-1">
            {item.description || 'Weekly subscription plan'}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            {item.deliveriesPerMonth} deliveries per month
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Frequency: {item.frequency}
          </p>
        </div>
      );
    }
    return null;
  };

  const validateForm = () => {
    const errors = {};
    if (!name.trim()) errors.name = 'Name is required';
    if (!email.trim()) errors.email = 'Email is required';
    if (!/\S+@\S+\.\S+/.test(email)) errors.email = 'Email is invalid';
    if (!address.trim()) errors.address = 'Address is required';
    if (!phone || phone.length < 10) errors.phone = 'Invalid phone number';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePhoneChange = (e) => {
    const input = e.target.value.replace(/\D/g, '');
    setPhone(input);
  };

  const getNextWednesday = () => {
    const today = new Date();
    const daysUntilWednesday = (3 - today.getDay() + 7) % 7;
    const nextWednesday = new Date(today);
    nextWednesday.setDate(today.getDate() + (daysUntilWednesday || 7));
    return nextWednesday.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleZelleOrder = async () => {
    if (!validateForm() || items.length === 0) return;

    setIsPaymentLoading(true);
    try {
      const nextWednesday = getNextWednesday();
      const emailContent = items.map(item => {
        if (item.isSubscription) {
          if (item.items) {
            return `${item.name}\nSelected Items:\n${item.items.map(subItem => 
              `- ${subItem.name} (2 oz weekly)`
            ).join('\n')}\nTotal: ${item.totalPrice.toFixed(2)}`;
          } else {
            return `${item.name}: ${item.totalPrice.toFixed(2)}`;
          }
        }
        return `${item.name}: ${item.totalPrice.toFixed(2)}`;
      }).join('\n\n');

      const templateParams = {
        to_email: email,
        to_name: name,
        to_address: address,
        to_phone: phone,
        cart_items: emailContent,
        total: calculateTotal().toFixed(2),
        payment_method: 'Zelle',
        delivery_date: nextWednesday,
        zelle_phone: '(279) 842-7951'
      };

      await emailjs.send(
        'service_ara3das', 
        'template_0q668xp', 
        templateParams
      );

      alert('Order placed successfully! Please complete your Zelle payment and check your email for confirmation.');
      clearCart();
      resetForm();
      setShowZelleInstructions(false);
    } catch (error) {
      console.error('Order error:', error);
      setPaymentError('Failed to place order. Please try again.');
    } finally {
      setIsPaymentLoading(false);
    }
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setPaymentError(null);
    
    if (validateForm() && items.length > 0) {
      setShowZelleInstructions(true);
    }
  };

  const resetForm = () => {
    setEmail('');
    setName('');
    setAddress('');
    setPhone('');
    setFormErrors({});
  };

  return (
    <div className="cart">
      <h2>Shopping Cart</h2>
      {items.length === 0 ? (
        <p>Your cart is empty. Start adding some delicious microgreens!</p>
      ) : (
        <>
          <div className="cart-summary">
            <h3>Cart Details</h3>
            <ul className="cart-items">
              {items.map((item) => (
                <li key={item.id} className="cart-item">
                  <div className="flex-grow">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">
                        {item.name}
                      </span>
                      <span className="font-medium">
                        ${(item.totalPrice || item.price).toFixed(2)}
                      </span>
                    </div>
                    {renderSubscriptionDetails(item)}
                  </div>
                  <button 
                    onClick={() => removeFromCart(item.id)} 
                    className="remove-item ml-4"
                    aria-label="Remove item"
                  >
                    <FaTrash />
                  </button>
                </li>
              ))}
            </ul>
            <p className="cart-total">Total: ${calculateTotal().toFixed(2)}</p>
          </div>

          <form onSubmit={handleCheckout} className="checkout-form">
            <div className="form-group">
              <input
                type="text"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              {formErrors.name && <span className="error">{formErrors.name}</span>}
            </div>

            <div className="form-group">
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {formErrors.email && <span className="error">{formErrors.email}</span>}
            </div>

            <div className="form-group">
              <input
                type="text"
                placeholder="Delivery Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
              />
              {formErrors.address && <span className="error">{formErrors.address}</span>}
            </div>

            <div className="form-group">
              <input
                type="tel"
                placeholder="Phone Number (10 digits)"
                value={phone}
                onChange={handlePhoneChange}
                maxLength="10"
                required
              />
              {formErrors.phone && <span className="error">{formErrors.phone}</span>}
            </div>

            {/* Zelle Payment Information */}
            <div className="payment-section">
              <h3>Payment Information</h3>
              <div className="zelle-payment-info">
                <h4>Zelle Payment Instructions</h4>
                <div className="zelle-details">
                  <p><strong>Zelle Phone:</strong> (279) 842-7951</p>
                  <p><strong>Amount:</strong> ${calculateTotal().toFixed(2)}</p>
                  <p><strong>Note:</strong> Please include your order details in the Zelle memo</p>
                </div>
              </div>

              {paymentError && <div className="payment-error">{paymentError}</div>}
            </div>

            <button 
              type="submit" 
              className="checkout-button"
              disabled={isPaymentLoading}
            >
              {isPaymentLoading ? 'Processing...' : 'Complete Order'}
            </button>
          </form>

          {/* Zelle Instructions Modal */}
          {showZelleInstructions && (
            <div className="modal-overlay" onClick={() => setShowZelleInstructions(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Complete Your Zelle Payment</h3>
                  <button 
                    className="modal-close"
                    onClick={() => setShowZelleInstructions(false)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                
                <div className="modal-body">
                  <div className="zelle-instructions">
                    <h4>Payment Details:</h4>
                    <div className="payment-summary">
                      <p><strong>Zelle Phone:</strong> (279) 842-7951</p>
                      <p><strong>Amount:</strong> ${calculateTotal().toFixed(2)}</p>
                      <p><strong>Order Total:</strong> ${calculateTotal().toFixed(2)}</p>
                    </div>
                    
                    <h4>How to Pay with Zelle:</h4>
                    <div className="zelle-methods">
                      <div className="zelle-method">
                        <h5>Method 1: Scan QR Code</h5>
                        <div className="qr-code-placeholder">
                          <p>📱 Scan this QR code with your bank's Zelle app</p>
                          <div className="qr-code">
                            <img src="/images/zelle-qr-code.png" alt="Zelle QR Code" />
                            <p className="qr-fallback">QR Code: (279) 842-7951</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="zelle-method">
                        <h5>Method 2: Manual Entry</h5>
                        <ol>
                          <li>Open your bank's mobile app or Zelle app</li>
                          <li>Select "Send Money with Zelle"</li>
                          <li>Enter phone: <strong>(279) 842-7951</strong></li>
                          <li>Enter amount: <strong>${calculateTotal().toFixed(2)}</strong></li>
                          <li>Add a memo: "Microgreens Order - {name}"</li>
                          <li>Complete the payment</li>
                        </ol>
                      </div>
                    </div>
                    
                    <div className="order-summary">
                      <h4>Your Order:</h4>
                      <ul>
                        {items.map((item) => (
                          <li key={item.id}>
                            {item.name} - ${(item.totalPrice || item.price).toFixed(2)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button 
                    className="btn-secondary"
                    onClick={() => setShowZelleInstructions(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn-primary"
                    onClick={handleZelleOrder}
                    disabled={isPaymentLoading}
                  >
                    {isPaymentLoading ? 'Processing...' : 'Confirm Order'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Cart;